import React, { useEffect, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';

const emptyForm = {
  staff_code: '',
  staff_name: '',
  department: 'HOUSEKEEPING',
  designation: '',
  mobile: '',
  email: '',
  shift: 'GENERAL',
  is_active: true
};

const DEPARTMENTS = ['HOUSEKEEPING', 'FRONT_OFFICE', 'MAINTENANCE', 'F_AND_B', 'SECURITY', 'OTHER'];
const SHIFTS = ['MORNING', 'AFTERNOON', 'NIGHT', 'GENERAL'];

function PmsStaffMaster({ token, selectedProperty }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();

  const headers = () => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  });

  const loadRows = async () => {
    if (!selectedProperty?.id) return setRows([]);
    try {
      const res = await fetch('/api/v1/pms/staff?all=1', { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load staff');
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || 'Failed to load staff');
    }
  };

  useEffect(() => {
    setError('');
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  const save = async () => {
    if (!selectedProperty?.id) return setError('Select property first');
    setMessage('');
    if (!form.staff_name.trim()) return setError('Staff Name is required');
    const mobileVal = String(form.mobile || '').trim();
    if (mobileVal && !/^\d{10}$/.test(mobileVal)) return setError('Mobile number must be exactly 10 digits');
    const emailVal = String(form.email || '').trim();
    if (emailVal && !/^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,10}$/.test(emailVal)) return setError('Enter a valid email address');
    setError('');
    try {
      const url = editingId ? `/api/v1/pms/staff/${editingId}` : '/api/v1/pms/staff';
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: headers(), body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Save failed');
      const label = editingId ? `Staff "${form.staff_name}" updated successfully` : `Staff "${form.staff_name}" created successfully`;
      setForm(emptyForm);
      setEditingId('');
      setMessage(label);
      loadRows();
    } catch (err) {
      setError(err?.message || 'Save failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Deactivate this staff member?')) return;
    setError('');
    try {
      const res = await fetch(`/api/v1/pms/staff/${id}`, { method: 'DELETE', headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Delete failed');
      loadRows();
    } catch (err) {
      setError(err?.message || 'Delete failed');
    }
  };

  const editRow = (row) => {
    setEditingId(String(row.staff_id));
    setError('');
    setMessage('');
    setForm({
      staff_code: row.staff_code || '',
      staff_name: row.staff_name || '',
      department: row.department || 'HOUSEKEEPING',
      designation: row.designation || '',
      mobile: String(row.mobile || '').replace(/\D/g, '').slice(0, 10),
      email: row.email || '',
      shift: row.shift || 'GENERAL',
      is_active: row.is_active !== false
    });
    scrollToForm();
  };

  return (
    <div style={{ padding: '1rem' }}>
      <h3 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>Staff Master</h3>
      {error ? <div className="error-banner">{error}</div> : null}
      {message ? <div style={{ background: '#f0fdf4', border: '1px solid #86efac', color: '#166534', padding: '0.5rem 1rem', borderRadius: 6, marginBottom: '0.5rem', fontSize: '0.875rem' }}>{message}</div> : null}

      <div style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#334155' }}>
        Manage staff members for housekeeping and other departments. Fields marked <span style={{ color: '#b91c1c' }}>*</span> are mandatory.
      </div>

      <div ref={formRef} className="filter-bar" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '0.6rem', ...editHighlightStyle(editHighlight) }}>
        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Staff Code</span>
          <input
            placeholder="Ex: HK001"
            value={form.staff_code}
            onChange={(e) => setForm({ ...form, staff_code: e.target.value })}
          />
        </label>

        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Staff Name <span style={{ color: '#b91c1c' }}>*</span></span>
          <input
            placeholder="Full name"
            value={form.staff_name}
            onChange={(e) => setForm({ ...form, staff_name: e.target.value })}
          />
        </label>

        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Department <span style={{ color: '#b91c1c' }}>*</span></span>
          <select value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} style={{ background: '#fff' }}>
            {DEPARTMENTS.map((d) => <option key={d} value={d}>{d.replace(/_/g, ' ')}</option>)}
          </select>
        </label>

        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Designation</span>
          <input
            placeholder="Ex: Room Attendant"
            value={form.designation}
            onChange={(e) => setForm({ ...form, designation: e.target.value })}
          />
        </label>

        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Mobile</span>
          <input
            placeholder="10-digit mobile number"
            value={form.mobile}
            maxLength={10}
            inputMode="numeric"
            onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, '') })}
          />
        </label>

        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Email</span>
          <input
            placeholder="Email address"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </label>

        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Shift</span>
          <select value={form.shift} onChange={(e) => setForm({ ...form, shift: e.target.value })} style={{ background: '#fff' }}>
            {SHIFTS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>

        <label style={{ display: 'grid', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Active</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            Active
          </span>
        </label>

        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'end' }}>
          <button onClick={save}>{editingId ? 'Update' : 'Create'}</button>
          <button onClick={() => { setEditingId(''); setForm(emptyForm); setError(''); setMessage(''); }}>Clear</button>
        </div>
      </div>

      <div style={{ marginTop: '0.8rem', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
          <thead>
            <tr style={{ background: 'var(--theme-primary)', color: '#fff' }}>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Code</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Name</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Department</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Designation</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Mobile</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Shift</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Status</th>
              <th style={{ padding: '0.45rem', textAlign: 'left' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.staff_id} style={{ borderBottom: '1px solid var(--theme-border)', opacity: r.is_active ? 1 : 0.55 }}>
                <td style={{ padding: '0.45rem' }}>{r.staff_code || '-'}</td>
                <td style={{ padding: '0.45rem' }}>{r.staff_name}</td>
                <td style={{ padding: '0.45rem' }}>{(r.department || '').replace(/_/g, ' ')}</td>
                <td style={{ padding: '0.45rem' }}>{r.designation || '-'}</td>
                <td style={{ padding: '0.45rem' }}>{r.mobile || '-'}</td>
                <td style={{ padding: '0.45rem' }}>{r.shift || 'GENERAL'}</td>
                <td style={{ padding: '0.45rem' }}>{r.is_active ? 'Active' : 'Inactive'}</td>
                <td style={{ padding: '0.45rem', display: 'flex', gap: '0.4rem' }}>
                  <button onClick={() => editRow(r)}>Edit</button>
                  {r.is_active ? <button onClick={() => remove(r.staff_id)}>Deactivate</button> : null}
                </td>
              </tr>
            ))}
            {!rows.length ? <tr><td style={{ padding: '0.6rem' }} colSpan={8}>No staff found</td></tr> : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PmsStaffMaster;
